/* eslint-disable react/jsx-one-expression-per-line */
import React, { Component } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import screenfull from 'screenfull';
import { isMobile, isMobileSafari } from 'react-device-detect';

import DownloadIcon from '@mui/icons-material/PublishOutlined';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNewSharp';
import ThumbnailsIcon from '../common/icons/thumbnails-icon.svg';
import SearchOffIcon from '../common/icons/search-off-icon.svg';
import ZoomInIcon from '../common/icons/zoom-in-icon.svg';
import ZoomOutIcon from '../common/icons/zoom-out-icon.svg';
import OnePageIcon from '../common/icons/single-page.svg';
import TwoPageIcon from '../common/icons/multi-page.svg';

import Thumbnails from './Thumbnails';
import Magazine from './Magazine';
import SearchBar from './SearchBar';
import LoadingOverlay from './LoadingOverlay';
import { MuPDFProvider } from './common/mupdf-context';
import { PageNavigationNext, PageNavigationPrevious } from './PageNavigation';
import { magazine } from './common/ducks';

let samplePdfLapti;
let samplePdfMedium;
let samplePdfGredi;
let samplePdfSlow;
let samplePdfLarge;
let samplePdfLinks;
let samplePdfSitowise;
let samplePdfStockmann;

/* eslint-disable global-require */
if (process.env.NODE_ENV === 'development') {
  // In webpack 5, file-loader may return {default: url} instead of a plain string
  const resolveAsset = m => (typeof m === 'string' ? m : m?.default || m);
  samplePdfLapti = resolveAsset(require('../../samples/sampleLapti.pdf'));
  samplePdfMedium = resolveAsset(require('../../samples/sampleMedium.pdf'));
  samplePdfGredi = resolveAsset(require('../../samples/sampleGredi.pdf'));
  samplePdfSlow = resolveAsset(require('../../samples/sampleSlow.pdf'));
  samplePdfLarge = resolveAsset(require('../../samples/sampleLarge.pdf'));
  samplePdfLinks = resolveAsset(require('../../samples/sampleLinks.pdf'));
  samplePdfSitowise = resolveAsset(require('../../samples/sampleSitowise.pdf'));
  samplePdfStockmann = resolveAsset(require('../../samples/sampleStockmann.pdf'));
}

const isDevEnv = process.env.NODE_ENV === 'development';

let uploadOwnDocuments = false;

class PdfViewer extends Component {
  magazineRef = React.createRef();

  constructor(props) {
    super(props);
    this.searchInputRef = React.createRef();

    const urlParams = window.location.search
      .substr(1)
      .split('&')
      .map(x => x.split('='));
    const fileParam = urlParams.find(x => x[0] === 'file');
    // JSP-file will put the content url in `window.fileUrl`
    let file =
      window.fileUrl || (fileParam && decodeURIComponent(fileParam[1]));
    if (!fileParam && isDevEnv) file = samplePdfSlow;

    const enhance = urlParams.find(x => x[0] === 'enhance');
    const upload = urlParams.find(x => x[0] === 'upload');
    const name = urlParams.find(x => x[0] === 'name');
    const urlOnePager = urlParams.find(x => x[0] === 'pageview');
    if (upload) uploadOwnDocuments = true;
    const pageNumber = urlParams.find(x => x[0] === 'page');
    if (pageNumber && Number(pageNumber[1]) >= 0 && props.setPageNumber)
      props.setPageNumber(Number(pageNumber[1]));
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('orientationchange', this.handleResize);
    window.addEventListener('load', this.handleResize);
    document.addEventListener('keydown', this.handleKeyDown, false);

    // prevent ios safari zooming and scrolling
    document.addEventListener('gesturestart', e => e.preventDefault(), {
      passive: false,
    });
    document.addEventListener('touchmove', e => e.preventDefault(), {
      passive: false,
    });

    const toggleStates = { 1: true, 2: false, 3: undefined };
    const onePageToggle =
      urlOnePager && urlOnePager[1] && toggleStates[urlOnePager[1]];

    this.state = {
      file,
      pdfArrayBuffer: null,
      isToolbarVisible: true,
      isSearchBarVisible: false,
      documentName: name && decodeURIComponent(name[1]),
      onePageToggle,
      // 0, 2 or undef: two page, 1: one page, 3: auto
      urlOnePager:
        (urlOnePager || []).length > 1 ? parseInt(urlOnePager[1], 10) : 3,
      enhance: !enhance || enhance[1] !== 'false',
    };
    this.clickCount = 0;
  }

  componentDidMount() {
    this.handleResize();
    this.loadFileDataForMuPDF(this.state.file);
    if (screenfull && screenfull.enabled) {
      screenfull.on('error', event => {
        console.error('Failed to enable fullscreen', event);
      });
      screenfull.on('change', () => {
        this.forceUpdate();
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.file !== this.state.file) {
      this.loadFileDataForMuPDF(this.state.file);
    }
  }

  componentWillUnmount() {
    if (screenfull && screenfull.enabled) {
      screenfull.off('error');
      screenfull.off('change');
    }
  }

  loadFileDataForMuPDF = async fileSource => {
    if (!fileSource) return;
    try {
      if (fileSource instanceof ArrayBuffer || fileSource instanceof Uint8Array) {
        this.setState({ pdfArrayBuffer: fileSource });
      } else if (typeof fileSource === 'string') {
        const resp = await fetch(fileSource);
        const buffer = await resp.arrayBuffer();
        this.setState({ pdfArrayBuffer: buffer });
      }
    } catch (err) {
      console.error('[PdfViewer] Failed to load PDF binary for MuPDF:', err);
    }
  };

  handleSearchButton = () => {
    const { isSearchBarVisible } = this.state;
    const {
      toggleThumbs,
      toggleSearchMode,
      setSearchInput,
      setSearchResult,
    } = this.props;

    this.setState({
      isSearchBarVisible: !isSearchBarVisible,
    });
    toggleSearchMode();
    if (!isSearchBarVisible) toggleThumbs(true);
    else {
      toggleThumbs(false);
      setSearchInput('');
      setSearchResult([]);
    }

    if (!isSearchBarVisible)
      setTimeout(() => this.searchInputRef.current.focus(), 200);
  };

  handleKeyDown = e => {
    const { showThumbs, nextPage, previousPage } = this.props;
    const { isSearchBarVisible } = this.state;
    if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
      e.preventDefault();
      this.handleSearchButton();
    }
    if (e.key === 'Enter' && isSearchBarVisible && isMobile) {
      this.searchInputRef.current.blur();
    }
    if (!showThumbs && document.activeElement !== this.searchInputRef.current) {
      if (e.key === 'ArrowLeft') previousPage();
      if (e.key === 'ArrowRight') nextPage();
    }
  };

  handleResize = () => {
    // TODO: Call handleResize on input blur, to ensure proper dimensions.
    // Relevant mostly if device rotated with input focused.
    if (document.activeElement === this.searchInputRef.current && isMobile) {
      return;
    }
    const {
      setViewportDimensions,
      setOnePagerMode,
      setSmallScreen,
    } = this.props;
    const { onePageToggle, urlOnePager } = this.state;

    const screenWidth = isMobileSafari
      ? window.innerWidth
      : document.documentElement.clientWidth;
    const screenHeight = isMobileSafari
      ? window.innerHeight
      : document.documentElement.clientHeight;

    // set body height. Necessary for some ios Safari versions
    window.document.body.style.height = `${screenHeight}px`;
    window.scroll(0, 0);

    setViewportDimensions(screenWidth, screenHeight);
    setSmallScreen(screenHeight <= 500 || screenWidth <= 500);
    setOnePagerMode(
      onePageToggle !== undefined ? Number(onePageToggle) : urlOnePager
    );
  };

  handleOnePagerSwitch = event => {
    this.setState({ onePageToggle: event.target.checked }, () => {
      this.handleResize();
    });
  };

  onDocumentLoadSuccess = ({ numPages }) => {
    const { setNumberOfPages } = this.props;
    setNumberOfPages(numPages);
  };

  handlePageRatioUpdate = (pageNumber, ratio) => {
    const { setOnePagerArray } = this.props;
    this.setState(prevState => {
      const pageRatios = { ...(prevState.pageRatiosMap || {}) };
      pageRatios[pageNumber] = ratio;

      // Build onePagerArray from known ratios
      const onePagerArray = [];
      const keys = Object.keys(pageRatios);
      keys.forEach(k => {
        onePagerArray[Number(k)] = pageRatios[k] > 1;
      });
      setOnePagerArray(onePagerArray);

      // Also build array format for Thumbnails
      const maxPage = Math.max(...keys.map(Number));
      const ratiosArray = [];
      for (let i = 0; i < maxPage; i += 1) {
        ratiosArray.push(pageRatios[i + 1] || 0.707);
      }

      return { pageRatiosMap: pageRatios, pageRatios: ratiosArray };
    });
  };

  handleChangeFile = e => {
    const fileReader = new FileReader();
    fileReader.onloadend = event => {
      const arrayBuffer = event.target.result;
      this.setState({ pdfArrayBuffer: arrayBuffer, file: URL.createObjectURL(new Blob([arrayBuffer], { type: 'application/pdf' })) });
    };
    if (e.target.files && e.target.files[0]) {
      fileReader.readAsArrayBuffer(e.target.files[0]);
    }
  };

  handleMagazineClick = () => {
    const { isToolbarVisible } = this.state;
    this.setState({
      isToolbarVisible: !isToolbarVisible,
    });
  };

  handleFullscreenClick = () => {
    if (screenfull.enabled) {
      screenfull.toggle();
      this.setState(state => state);
    }
  };

  handleMagazineClicks = () => {
    if (this.timeout) clearTimeout(this.timeout);
    this.clickCount += 1;
    this.timeout = setTimeout(() => {
      if (this.clickCount === 1) {
        this.setState(state => ({
          isToolbarVisible: !state.isToolbarVisible,
        }));
      }
      this.clickCount = 0;
    }, 250);
  };

  handleZoomClick = () => {
    const { toggleZoom } = this.props;
    if (
      this.magazineRef.current &&
      this.magazineRef.current.transformRef.current
    ) {
      const transform = this.magazineRef.current.transformRef.current;
      const scale = transform?.state?.scale ?? transform?.instance?.transformState?.scale ?? 1;
      const zoomedOut = scale <= 1;
      if (zoomedOut) {
        transform.zoomIn(0.7);
        toggleZoom(true, 2.01);
      } else {
        transform.centerView(1);
        toggleZoom(false, 1);
      }
    }
  };

  render() {
    const {
      file,
      pdfArrayBuffer,
      isToolbarVisible,
      isSearchBarVisible,
      onePageToggle,
      enhance,
      pageRatios,
    } = this.state;
    const { toggleThumbs, isSmallScreen, showThumbs, zoom } = this.props;

    return (
      <MuPDFProvider
        fileData={pdfArrayBuffer}
        onLoad={this.onDocumentLoadSuccess}
        onError={e => console.error('[PdfViewer] PDF load error:', e)}
      >
        <LoadingOverlay />
        <Options>
          {(isDevEnv || uploadOwnDocuments) && (
            <input type="file" accept="pdf" onChange={this.handleChangeFile} />
          )}
        </Options>
        <div className="pdf-document">
          <Magazine
            onClick={this.handleMagazineClicks}
            ref={this.magazineRef}
            enhance={enhance}
            onPageRatioUpdate={this.handlePageRatioUpdate}
          />
          <PageNavigationPrevious handleResize={this.handleResize} />
          <PageNavigationNext handleResize={this.handleResize} />

          <TransitionGroup>
            {(!isSmallScreen || (isToolbarVisible && !showThumbs)) && (
              <CSSTransition
                classNames="toolbox"
                timeout={400}
              >
                <ButtonContainer>
                <Button onClick={toggleThumbs} title="Thumbnails">
                  <img src={ThumbnailsIcon} alt="Thumbnails" />
                </Button>
                {zoom ? (
                  <Button onClick={this.handleZoomClick} title="Zoom out">
                    <img src={ZoomOutIcon} alt="Zoom out" />
                  </Button>
                ) : (
                  <Button onClick={this.handleZoomClick} title="Zoom in">
                    <img src={ZoomInIcon} alt="Zoom in" />
                  </Button>
                )}
                {onePageToggle ? (
                  <Button
                    onClick={() =>
                      this.handleOnePagerSwitch({ target: { checked: false } })
                    }
                    title="Magazine mode"
                  >
                    <img src={TwoPageIcon} alt="Magazine mode" />
                  </Button>
                ) : (
                  <Button
                    onClick={() =>
                      this.handleOnePagerSwitch({ target: { checked: true } })
                    }
                    title="Single page mode"
                  >
                    <img src={OnePageIcon} alt="Single page mode" />
                  </Button>
                )}
                {screenfull.enabled &&
                  (!screenfull.isFullscreen ? (
                    <FullscreenIcon
                      type="button"
                      onClick={this.handleFullscreenClick}
                      titleAccess="Fullscreen"
                    />
                  ) : (
                    <FullscreenExitIcon
                      type="button"
                      onClick={this.handleFullscreenClick}
                      titleAccess="Exit fullscreen"
                    />
                  ))}
                {window.location !== window.parent.location && (
                  <Link
                    href={window.location.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <OpenInNewIcon
                      type="button"
                      titleAccess="Open in a new window"
                    />
                  </Link>
                )}
                <Link href={file} download>
                  <StyledDownloadIcon
                    type="button"
                    titleAccess="Download PDF"
                  />
                </Link>
                {isSearchBarVisible ? (
                  <Button onClick={this.handleSearchButton} title="Search off">
                    <img src={SearchOffIcon} alt="Search off" />
                  </Button>
                ) : (
                  <Button onClick={this.handleSearchButton} title="Search">
                    <SearchIcon />
                  </Button>
                )}
              </ButtonContainer>
              </CSSTransition>
            )}
          </TransitionGroup>
          <TransitionGroup>
            {isSearchBarVisible && (
              <CSSTransition
                classNames="searchbar"
                timeout={600}
              >
              <SearchBar
                onClose={this.handleSearchButton}
                ref={this.searchInputRef}
              />
              </CSSTransition>
            )}
          </TransitionGroup>
          <Thumbnails pageRatios={pageRatios} />
        </div>
      </MuPDFProvider>
    );
  }
}

const ButtonContainer = styled.div`
  position: absolute;
  bottom: 0;
  display: flex;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px;
  padding-bottom: ${() => (isMobileSafari ? '10px' : '3px')};
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;

  & > * {
    cursor: pointer;
  }

  svg {
    width: 30px;
    height: 30px;
    margin: 0px 5px;
    fill: black;
  }

  img {
    max-width: none;
    height: 30px;
    width: 30px;
    margin: 0px 5px;
  }
`;

const StyledDownloadIcon = styled(DownloadIcon)`
  && {
    transform: rotate(180deg);
    padding: 0;
  }
`;

const Options = styled.div`
  position: absolute;
  top: 0;
  right: 0.5rem;
  max-width: 500px;
`;

const Link = styled.a`
  color: black;

  svg {
    padding: 2px;
  }
`;

const Button = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin: 0;
`;

const mapStateToProps = state => ({
  isSmallScreen: state.magazine.isSmallScreen,
  showThumbs: state.magazine.showThumbs,
  zoom: state.magazine.zoom,
  viewportDimensions: state.magazine.viewportDimensions,
  isSearchMode: state.magazine.isSearchMode,
  pageNumber: state.magazine.pageNumber,
  numberOfPages: state.magazine.numberOfPages,
});

export default connect(
  mapStateToProps,
  {
    nextPage: magazine.nextPage,
    previousPage: magazine.previousPage,
    toggleThumbs: magazine.toggleThumbs,
    toggleZoom: magazine.toggleZoom,
    toggleSearchMode: magazine.toggleSearchMode,
    setNumberOfPages: magazine.setNumberOfPages,
    setOnePagerMode: magazine.setOnePagerMode,
    setOnePagerArray: magazine.setOnePagerArray,
    setViewportDimensions: magazine.setViewportDimensions,
    setSmallScreen: magazine.setSmallScreen,
    setSearchInput: magazine.setSearchInput,
    setSearchResult: magazine.setSearchResult,
    setPageNumber: magazine.setPageNumber,
  }
)(PdfViewer);
